import type { SimpleFieldConfig, SimpleTableConfigBase, BaseEntity } from "../types";
import type { FilterState, RangeFilterValue } from "@/components/shared/types";
import {
  NEVER_LAST_ACTIVITY_PRESET_LABEL,
  NEVER_LAST_ACTIVITY_PRESET_VALUE,
} from "@/lib/utils/formatting/datetime-presets";

/**
 * Converts plain option objects to the shape expected by ResponsiveCommandSelect/MultiSelect.
 */
export function toCommandOptions(
  options: Array<{ value: string; label: string }>
): Array<{ value: string; label: string; searchableText: string }> {
  return options.map((option) => ({
    value: option.value,
    label: option.label,
    searchableText: `${option.label} ${option.value}`.trim(),
  }));
}

/**
 * Maps configured preset IDs to dropdown options.
 * Includes the synthetic `never` entry for last-activity fields.
 */
export function resolveDatetimePresetSelections(
  allPresets: Array<{ value: string; label: string }>,
  presetValues?: string[]
): Array<{ value: string; label: string }> {
  if (!presetValues) {
    return allPresets.map((p) => ({ value: p.value, label: p.label }));
  }
  return presetValues
    .map((value) => {
      const match = allPresets.find((p) => p.value === value);
      if (match) return { value: match.value, label: match.label };
      if (value === NEVER_LAST_ACTIVITY_PRESET_VALUE) {
        return { value: NEVER_LAST_ACTIVITY_PRESET_VALUE, label: NEVER_LAST_ACTIVITY_PRESET_LABEL };
      }
      return null;
    })
    .filter((entry): entry is { value: string; label: string } => entry != null);
}

export interface QuickFilterField {
  id: string;
  label: string;
  fieldConfig: SimpleFieldConfig;
}

/**
 * Returns fields that qualify as "quick filters" (select-list only).
 * Excludes range inputs, free-text filters, and fields without options/select type.
 */
export function getQuickFilterFields<T extends BaseEntity = BaseEntity>(
  config: SimpleTableConfigBase<T>
): QuickFilterField[] {
  if (!config.features?.filters || !config.fields) return [];

  return Object.entries(config.fields)
    .filter(([_, fieldConfig]) => fieldConfig.filterable !== false && !fieldConfig.formOnly)
    .filter(([_, fieldConfig]) => {
      if (fieldConfig.filterType === "range") return false;
      if (fieldConfig.filterType === "company-select") return true;
      if (fieldConfig.filterType === "client-select") return true;
      if (fieldConfig.filterType === "user-select") return true;
      if (
        (fieldConfig.type === "datetime" || fieldConfig.type === "date") &&
        fieldConfig.datetimePresets?.enabled
      )
        return true;
      if (
        (fieldConfig.type === "tags" || fieldConfig.type === "json") &&
        fieldConfig.options &&
        fieldConfig.options.length > 0
      )
        return true;
      if (fieldConfig.options && fieldConfig.options.length > 0) return true;
      if (fieldConfig.type === "boolean") return true;
      return false;
    })
    .map(([fieldId, fieldConfig]) => ({
      id: fieldId,
      label: fieldConfig.label || fieldId,
      fieldConfig,
    }));
}

/**
 * Rebuild a range filter object from the keys `RangeFilterValue` allows.
 * Returns null when the object carries none of them.
 */
function pickRangeFilterValue(value: object): RangeFilterValue | null {
  const source: Record<string, unknown> = { ...value };
  const out: Record<string, string> = {};
  for (const key of ["min", "max", "start", "end", "from", "to"]) {
    const entry = source[key];
    if (typeof entry === "string") out[key] = entry;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Split an untyped filter map (FilterContext stores values as `unknown`) into
 * the `FilterState` the filter inputs accept plus the leftovers they can't
 * represent. Nothing is cast, and nothing is silently lost: callers merge
 * `extras` back under the input's result so an unrenderable filter survives an
 * edit of a different field.
 */
export function partitionFilterState(filters: Record<string, unknown>): {
  state: FilterState;
  extras: Record<string, unknown>;
} {
  const state: FilterState = {};
  const extras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" || typeof value === "boolean") {
      state[key] = value;
    } else if (Array.isArray(value) && value.every((v): v is string => typeof v === "string")) {
      state[key] = value;
    } else if (typeof value === "object") {
      const range = pickRangeFilterValue(value);
      if (range) {
        state[key] = range;
      } else {
        extras[key] = value;
      }
    } else {
      extras[key] = value;
    }
  }
  return { state, extras };
}
